<template>
  <Transition name="intro" :duration="MOTION_SLOW_MS">
    <div
      v-if="showIntro"
      role="dialog"
      aria-modal="true"
      :aria-label="i18n(translations.headline)"
      class="fixed inset-0 z-50 grid place-items-center p-[--raw-edge-space]"
    >
      <!-- Pointer-only duplicate of the card's ✕, hidden from assistive tech; it fades
           itself — see the style block for why not the parent. -->
      <button
        type="button"
        tabindex="-1"
        aria-hidden="true"
        class="absolute inset-0 bg-[white]/10 backdrop-blur-[80px]"
        @click="handleSkip"
      />

      <!-- Back Market wordmark → localised storefront, desktop only. -->
      <a
        v-if="!isWebview"
        :href="i18n(backMarket.url)"
        target="_blank"
        rel="noopener"
        :aria-label="i18n(backMarket.label)"
        class="absolute left-[17px] top-0 hidden h-[--raw-header-height] items-center lg:flex"
        @click="track('backmarket-home')"
      >
        <RevIllustration
          src="/mine-my-phone/back-market_logo.svg"
          :width="134"
          :height="16"
          alt=""
          class="h-16"
        />
      </a>

      <!-- Three rows: ✕ · stage · chrome. The card takes the height its step needs;
           `max-h` is the only limit, so a short viewport scrolls the stage instead. -->
      <div
        data-intro-card
        class="raw-glass relative grid max-h-[calc(100dvh-var(--raw-edge-space)*2)] w-[min(680px,100%)] grid-rows-[auto_minmax(0,1fr)_auto] gap-8 rounded-[20px] p-20 text-center md:p-32"
      >
        <!-- The campaign's canonical ✕ (chassis from `UI/DetailHeader.vue`), in flow so it
             can never overlap a step's media; named *Skip*, which is what it does. -->
        <button
          type="button"
          class="raw-glass rounded-full flex h-36 w-36 shrink-0 cursor-pointer items-center justify-center justify-self-end text-[--raw-darkgrey] transition-colors duration-[var(--raw-fast)] ease-[var(--raw-ease-out-quart)] active:border-[white] active:bg-[white] md:hover:border-[white] md:hover:bg-[white]"
          :aria-label="i18n(translations.skip)"
          @click="handleSkip"
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M1 1L11 11M11 1L1 11"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </button>

        <!-- A scrollport only once the card hits `max-h`; the step centres itself
             (`min-h-full`) because centred overflow can't be scrolled back to the top. -->
        <div data-stage class="overflow-y-auto overflow-x-hidden">
          <!-- `out-in`: the card empties for a beat, so each step reads as a new screen.
               One component per layout, in `./steps` — adding a screen is an entry in
               `STEPS` plus one branch here. -->
          <Transition name="step" mode="out-in">
            <div
              :key="current.key"
              data-step
              class="grid min-h-full place-items-center content-center gap-20"
            >
              <HeroStep
                v-if="current.layout === 'hero'"
                :headline="current.headline"
                :subtitle="current.subtitle"
                :body="current.body"
              />
              <FilmStep
                v-else-if="current.layout === 'video'"
                :title="current.title"
                @play="track('video')"
              />
              <TutorialStep
                v-else-if="current.layout === 'ambient'"
                :body="current.body"
                :video-name="current.videoName"
              />
              <ChoiceStep
                v-else-if="current.layout === 'choice'"
                :title="current.title"
                :body="current.body"
                @start="handleStart"
              />
            </div>
          </Transition>
        </div>

        <!-- Fixed chrome, so the CTA never remounts and Enter keeps stepping through.
             Each control names its own column: an absent one leaves a 0-width track
             instead of shifting the others into it. -->
        <div
          class="mt-24 grid grid-cols-[1fr_auto_1fr] items-center gap-8 md:gap-16"
        >
          <button
            v-if="step > 0"
            type="button"
            class="raw-cta-3 col-start-1 cursor-pointer justify-self-start opacity-40 transition-opacity duration-[var(--raw-fast)] ease-[var(--raw-ease-out-quart)] hover:opacity-70"
            @click="handleBack"
          >
            {{ i18n(translations.back) }}
          </button>

          <Button
            v-if="advanceCta"
            variant="tertiary"
            class="col-start-2 min-w-[140px] justify-self-center border-[--raw-black] bg-[--raw-black] px-24 py-10 transition-colors duration-[var(--raw-fast)] ease-[var(--raw-ease-out-quart)] hover:border-[--raw-verydarkgrey] hover:bg-[--raw-verydarkgrey] active:border-[--raw-verydarkgrey] active:bg-[--raw-verydarkgrey] md:min-w-[180px]"
            @click="handleAdvance"
          >
            <!-- Colour on the copy: on the button it ties with the DS variant's own
                 `text-*` and the winner falls to stylesheet order. -->
            <span class="raw-cta-1 text-[white]">
              {{ i18n(advanceCta) }}
            </span>
          </Button>

          <!-- `tabular-nums` so it can't jitter between steps; the live region announces
               the same thing to a screen reader. -->
          <p class="raw-cta-3 col-start-3 justify-self-end opacity-40">
            <span aria-hidden="true" class="whitespace-nowrap tabular-nums">
              {{ i18n(translations.stepCounter, counter) }}
            </span>
            <span class="sr-only" aria-live="polite">
              {{ i18n(translations.stepProgress, progress) }}
            </span>
          </p>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useIsWebview } from '@backmarket/nuxt-module-navigation/useIsWebview'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevIllustration } from '@ds/components/Illustration'
import { onKeyStroke } from '@vueuse/core'

import Button from '~/scopes/campaign/components/UI/Button.vue'
import ChoiceStep from '~/scopes/campaign/components/intro/steps/ChoiceStep.vue'
import FilmStep from '~/scopes/campaign/components/intro/steps/FilmStep.vue'
import HeroStep from '~/scopes/campaign/components/intro/steps/HeroStep.vue'
import TutorialStep from '~/scopes/campaign/components/intro/steps/TutorialStep.vue'
import { useIntroState } from '~/scopes/campaign/composables/useIntroState'
import { useModalState } from '~/scopes/campaign/composables/useModalState'
import backMarket from '~/scopes/campaign/const/back-market.translations'
import {
  MOTION_SLOW_MS,
  type ModalRoute,
} from '~/scopes/campaign/const/config.const'
import type { I18nDescriptor } from '~/scopes/campaign/data/metals/types'
import { ONBOARDING_VIDEO } from '~/scopes/campaign/data/videos'

import translations from './IntroModal.translations'

const i18n = useI18n()
const { trackClick } = useTracking()
const { showIntro, dismissIntro } = useIntroState()
const { activeCinema, navigate } = useModalState()
const isWebview = useIsWebview()

// Discriminated by `layout` so a step carries exactly the copy its component takes — the
// closing step has no `cta`, which is what lets the template pass `current.*` unguarded.
type IntroStep = { key: string } & (
  | {
      layout: 'hero'
      cta: I18nDescriptor
      headline: I18nDescriptor
      subtitle: I18nDescriptor
      body: I18nDescriptor
    }
  | { layout: 'video'; cta: I18nDescriptor; title: I18nDescriptor }
  | {
      layout: 'ambient'
      cta: I18nDescriptor
      /** Asset base name — the sources are resolved per locale at render. */
      videoName: string
      body: I18nDescriptor
    }
  | { layout: 'choice'; title: I18nDescriptor; body: I18nDescriptor }
)

// The tour, and the only place its copy is named: a screen is a step here plus a
// component in `./steps`, and the chrome reads the length.
const STEPS: readonly IntroStep[] = [
  {
    key: 'start',
    layout: 'hero',
    headline: translations.headline,
    subtitle: translations.step1Subtitle,
    body: translations.step1Body,
    cta: translations.ctaStart,
  },
  {
    key: 'film',
    layout: 'video',
    title: translations.headline,
    cta: translations.ctaNext,
  },
  {
    key: 'clip',
    layout: 'ambient',
    videoName: ONBOARDING_VIDEO,
    body: translations.step3Body,
    cta: translations.ctaNext,
  },
  {
    key: 'choice',
    layout: 'choice',
    title: translations.lastTitle,
    body: translations.lastBody,
  },
]

const step = ref(0)
const current = computed(() => STEPS[step.value])

// `null` on the closing step, where the cards are the forward move and no CTA renders.
const advanceCta = computed(() =>
  current.value.layout === 'choice' ? null : current.value.cta,
)

// The screen-reader sentence reads the plain numbers; the indicator zero-pads them.
const progress = computed(() => ({
  current: step.value + 1,
  total: STEPS.length,
}))
const counter = computed(() => ({
  current: String(progress.value.current).padStart(2, '0'),
  total: String(progress.value.total).padStart(2, '0'),
}))

// Every control in here reports to the same zone.
const track = (name: string) => trackClick({ zone: 'intro', name })

// Rearm on OPEN, not on close: the component never unmounts, and resetting inside
// `dismissIntro` would flash step 1 through the card's leave transition.
watch(showIntro, (visible) => {
  if (visible) step.value = 0
})

function handleAdvance() {
  track(`${current.value.key}-next`)
  step.value += 1
}

// No floor to guard: the template omits Back on the first step.
function handleBack() {
  track(`${current.value.key}-back`)
  step.value -= 1
}

// The ✕, the backdrop and Escape all land here: persist the seen-flag and let `showIntro`
// flip, on whatever route we were already on.
function handleSkip() {
  track(`${current.value.key}-skip`)
  dismissIntro()
}

// The other exit: navigate first so the app mounts straight into that impact, then dismiss.
function handleStart(route: ModalRoute) {
  track(`start-${route}`)
  navigate(route)
  dismissIntro()
}

// Escape skips, except while the film is in cinema mode: that layer sits above this modal
// and answers Escape itself (`UI/NativeVideo.vue`).
onKeyStroke('Escape', (event) => {
  if (!showIntro.value || activeCinema.value) return
  event.preventDefault()
  handleSkip()
})
</script>

<style scoped>
/* The children fade THEMSELVES, never the root: an ancestor's opacity would kill their
   `backdrop-filter` (full reasoning in `UI/ExtendedModal.vue`). */
.intro-enter-active > *,
.intro-leave-active > * {
  transition: opacity var(--raw-out-quart--slow);
}
.intro-enter-from > *,
.intro-leave-to > * {
  opacity: 0;
}
.intro-enter-active [data-intro-card],
.intro-leave-active [data-intro-card] {
  transition:
    opacity var(--raw-out-quart--slow),
    transform var(--raw-out-quart--slow);
}
.intro-enter-from [data-intro-card],
.intro-leave-to [data-intro-card] {
  transform: scale(90%);
}

/* Step to step: out, then in, so the ~70ms blank reads as "a new screen" instead of two
   similar steps dissolving into each other; `mode="out-in"` also keeps the film and the
   clip from ever playing at once. The out is shorter than the DS's fastest token because
   it starts on the click, and that is what answers the press. */
.step-leave-active {
  transition: opacity 200ms var(--raw-ease-out-quart);
}
.step-enter-active {
  transition: opacity var(--raw-out-quart--fast);
}
.step-enter-from,
.step-leave-to {
  opacity: 0;
}

/* The step swap needs no reduced-motion variant: it is already pure opacity. */
@media (prefers-reduced-motion: reduce) {
  .intro-enter-from [data-intro-card],
  .intro-leave-to [data-intro-card] {
    transform: none;
  }
}
</style>
